import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from 'react-router-dom';
import { getUserTickets } from "../../../redux/auth/action/userTicketActions";
import PurchasedTicketModal from "./PurchasedTicketModal";
import { FaTicketAlt, FaHistory, FaSearch, FaQrcode, FaHome, FaArrowLeft } from "react-icons/fa";

const PLACEHOLDER_IMG = "/placeholder-event.png";

const PurchaseHistory = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  // Fetch tickets from Redux store
  const { tickets, loading, error } = useSelector((state) => state.ticketList);

  // Filter tickets to show only completed tickets with QR codes
  const completedTickets = (tickets || []).filter(
    ticket => ticket.payment_status === 'completed' && (ticket.qr_code || ticket.qr_code_url)
  );

  // Sort tickets by newest first (using created_at or purchase_date)
  const sortedTickets = [...completedTickets].sort((a, b) => {
    // Try to use purchase_date first, then created_at, then fallback to id
    const dateA = a.purchase_date || a.created_at || a.id;
    const dateB = b.purchase_date || b.created_at || b.id;
    
    // If using dates, convert to timestamp for comparison
    if (typeof dateA === 'string' && typeof dateB === 'string') {
      return new Date(dateB) - new Date(dateA); // Newest first
    }
    
    // Fallback to numeric comparison (higher ID = newer)
    return dateB - dateA;
  });

  // Search filter
  const filteredTickets = sortedTickets.filter(ticket => {
    const term = searchTerm.toLowerCase();
    return (
      (ticket.event_name && ticket.event_name.toLowerCase().includes(term)) ||
      (ticket.venue_name && ticket.venue_name.toLowerCase().includes(term)) ||
      (ticket.reference_id && ticket.reference_id.toLowerCase().includes(term))
    );
  });

  useEffect(() => {
    dispatch(getUserTickets());
  }, [dispatch]);

  // Modal handlers
  const handleTicketClick = (ticket) => {
    setSelectedTicket(ticket);
    setIsModalOpen(true);
  };
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedTicket(null);
  };

  // Pagination (optional, simple client-side)
  const pageSize = 9;
  const totalPages = Math.ceil(filteredTickets.length / pageSize);
  const paginatedTickets = filteredTickets.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const loadNextPage = () => setCurrentPage((p) => Math.min(p + 1, totalPages));
  const loadPrevPage = () => setCurrentPage((p) => Math.max(p - 1, 1));

  return (
    <div className="py-6">
      {/* Navigation Bar */}
      <div className="bg-white shadow-sm mb-6 p-3 rounded-lg flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button 
            onClick={() => navigate('/dashboard')}
            className="flex items-center text-[#0056A6] hover:text-[#FF9000] transition"
          >
            <FaArrowLeft className="mr-2" /> Back to Dashboard
          </button>
          <Link to="/" className="flex items-center text-[#0056A6] hover:text-[#FF9000] transition">
            <FaHome className="mr-2" /> Home
          </Link>
        </div>
        <div>
          <span className="text-sm text-gray-500">
            {filteredTickets.length} {filteredTickets.length === 1 ? 'ticket' : 'tickets'} found
          </span>
        </div>
      </div>
      
      {/* Section Header */}
      <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center">
            <FaHistory className="mr-3 text-[#0056A6]" />
            Your Purchase History
          </h1>
          <p className="text-gray-600 mt-2">
            View all your completed ticket purchases
          </p>
        </div>
        {/* Search Field */}
        <div className="flex items-center bg-white border border-gray-300 rounded-full px-3 py-2 shadow-sm w-full md:w-80">
          <FaSearch className="text-gray-400 mr-2" />
          <input
            type="text"
            placeholder="Search by event, venue, or reference"
            value={searchTerm}
            onChange={e => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            className="flex-1 outline-none bg-transparent text-gray-700"
          />
        </div>
      </div>

      {loading && (
        <div className="py-8 text-center text-lg text-gray-500">Loading tickets...</div>
      )}

      {!loading && paginatedTickets.length > 0 && (
        <>
          {/* Ticket Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
            {paginatedTickets.map((ticket) => (
              <div
                key={ticket.id}
                className="bg-white rounded-xl shadow-md hover:shadow-lg transition p-4 flex flex-col items-center cursor-pointer border border-gray-100"
                onClick={() => handleTicketClick(ticket)}
              >
                <div className="relative w-full h-40 mb-3">
                  <img
                    src={ticket.event_image || PLACEHOLDER_IMG}
                    alt={ticket.event_name}
                    className="w-full h-40 object-cover rounded-lg border"
                    onError={e => { e.target.src = PLACEHOLDER_IMG; }}
                  />
                  <span className="absolute bottom-2 right-2 bg-white bg-opacity-80 rounded-full p-1 shadow">
                    <FaQrcode className="text-[#0056A6] text-lg" />
                  </span>
                </div>
                <h3 className="text-lg font-semibold text-center mb-1">{ticket.event_name}</h3>
                <div className="text-gray-500 text-sm mb-2">{ticket.venue_name}</div>
                <div className="text-xs text-gray-400 mb-2">
                  {ticket.start_time} - {ticket.end_time}
                </div>
                <span className="bg-green-100 text-green-700 px-2 py-1 rounded-full text-xs font-semibold mb-2">
                  Completed
                </span>
                <button
                  className="mt-auto bg-[#FF9000] hover:bg-orange-600 text-white font-semibold py-1 px-4 rounded-full transition"
                  onClick={e => { e.stopPropagation(); handleTicketClick(ticket); }}
                >
                  View Ticket
                </button>
              </div>
            ))}
          </div>
          {/* Pagination */}
          <div className="flex justify-center mt-8 space-x-4">
            <button
              onClick={loadPrevPage}
              disabled={currentPage === 1}
              className={`px-4 py-2 rounded ${currentPage === 1 ? 'bg-gray-300 cursor-not-allowed' : 'bg-[#0056A6] hover:bg-blue-700 text-white'}`}
            >
              Previous
            </button>
            <span className="px-4 py-2 bg-gray-100 rounded">Page {currentPage} of {totalPages}</span>
            <button
              onClick={loadNextPage}
              disabled={currentPage === totalPages}
              className={`px-4 py-2 rounded ${currentPage === totalPages ? 'bg-gray-300 cursor-not-allowed' : 'bg-[#0056A6] hover:bg-blue-700 text-white'}`}
            >
              Next
            </button>
          </div>
          {/* Modal */}
          <PurchasedTicketModal
            ticket={selectedTicket}
            isOpen={isModalOpen}
            onClose={handleCloseModal}
          />
          {error && <div className="bg-red-50 text-red-700 p-4 rounded-lg mt-4">{error}</div>}
        </>
      )}

      {!loading && paginatedTickets.length === 0 && (
        <div className="bg-gray-50 rounded-xl p-12 flex flex-col items-center justify-center text-center shadow-sm">
          <FaTicketAlt size={48} className="text-gray-300 mb-6" />
          <h2 className="text-2xl font-semibold text-gray-700 mb-2">No Completed Tickets Yet</h2>
          <p className="text-gray-500 mb-6 max-w-md">
            You don't have any completed ticket purchases. Check out upcoming events to find your next experience!
          </p>
          <Link
            className="bg-[#0056A6] hover:bg-[#FF9000] text-white font-medium py-3 px-8 rounded-full transition duration-300 shadow-md"
            to="/"
          >
            Browse Events
          </Link>
        </div>
      )}

      <div className="flex justify-between items-center mt-8">
        <button
          onClick={() => navigate('/dashboard')}
          className="bg-gray-200 hover:bg-gray-300 text-gray-700 font-bold py-2 px-4 rounded transition"
        >
          Back to Dashboard
        </button>
        <button
          onClick={() => dispatch(getUserTickets())}
          className="bg-[#0056A6] hover:bg-[#FF9000] text-white font-bold py-2 px-4 rounded transition"
        >
          Refresh Tickets
        </button>
      </div>
    </div>
  );
};

export default PurchaseHistory;